import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { trackClientEvent } from 'analytics/analytics'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'

const FilterBand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${(props) => props.theme.palette.neutral.surface.lightBackground};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(12)};
    padding: ${rem(12)} ${rem(20)};
    border-radius: ${(props) => props.theme.borderRadius.L};
  }
`

const ChipsCarousel = styled(Carousel)`
  align-self: stretch;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    align-self: auto;
  }
`

interface Props {
  breakfastIncluded: boolean
  freeCancellation: boolean
}

function toggleStateToClientEventAction(toggleState: boolean): 'toggled_on' | 'toggled_off' {
  return toggleState ? 'toggled_on' : 'toggled_off'
}

function PropertyQuickFilters({ breakfastIncluded, freeCancellation }: Props) {
  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const showPackagesWithLabel = intl.formatMessage({
    id: 'mN_Mn43wvhDnTmjLzo0P',
    defaultMessage: 'Show packages with:',
    description: 'Leading label introducing the row of package quick-filter chips on the offer page',
  })
  const breakfastIncludedLabel = intl.formatMessage({
    id: 'w2jEyEHf_MDWoniUCBnT',
    defaultMessage: 'Breakfast included',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with breakfast included',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'Cnk7NVK8VWdi1ubUWdxY',
    defaultMessage: 'Free cancellation',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with free cancellation',
  })

  const toggleBreakfastIncluded = useCallback(() => {
    const newToggleState = !breakfastIncluded
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { breakfastIncluded: newToggleState },
    })
    trackClientEvent({
      subject: 'breakfast_included_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [breakfastIncluded, offerPageDispatch])
  const toggleFreeCancellation = useCallback(() => {
    const newToggleState = !freeCancellation
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { freeCancellation: newToggleState },
    })
    trackClientEvent({
      subject: 'free_cancellation_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [freeCancellation, offerPageDispatch])

  return <FilterBand>
    <BodyText variant="medium" weight="bold">
      {showPackagesWithLabel}
    </BodyText>
    <ChipsCarousel gutterStyle="overflow" gap={8}>
      <FilterChip
        size="medium"
        selected={breakfastIncluded}
        startIcon={breakfastIncluded ? <LineCheckIcon /> : <LineCoffeeIcon />}
        onClick={toggleBreakfastIncluded}
      >
        {breakfastIncludedLabel}
      </FilterChip>
      <FilterChip
        size="medium"
        selected={freeCancellation}
        startIcon={freeCancellation ? <LineCheckIcon /> : <LineShieldCheckIcon />}
        onClick={toggleFreeCancellation}
      >
        {freeCancellationLabel}
      </FilterChip>
    </ChipsCarousel>
  </FilterBand>
}

type MappedProps = Props

export default withOfferPageState((state): MappedProps => ({
  breakfastIncluded: !!state.quickFilters?.breakfastIncluded,
  freeCancellation: !!state.quickFilters?.freeCancellation,
}), PropertyQuickFilters)
